import React, { useState, useEffect } from 'react';
import {
  Mail,
  Plus,
  Trash2,
  Power,
  Loader2,
  RefreshCw,
  AlertCircle,
  Send,
  CheckCircle2,
  XCircle,
  Eye,
  Server,
  Cloud,
  Settings,
  Bell,
  Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { emailConfigAPI } from '../../services/api';
import { cn } from '../../lib/utils';
import { toast } from '../../hooks/use-toast';
import ConfirmDialog from '../../components/ui/confirm-dialog';
import { useConfirm } from '../../components/ui/use-confirm';

const EmailConfigPage = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { confirm, dialogProps } = useConfirm();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isLogsOpen, setIsLogsOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [newConfig, setNewConfig] = useState({
    name: '',
    provider_type: 'SMTP',
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_use_tls: true,
    aws_access_key: '',
    aws_secret_key: '',
    aws_region: 'us-east-1',
    from_email: '',
    from_name: 'CRM System',
    reply_to_email: ''
  });

  const notificationEvents = [
    { key: 'notify_lead_created', label: 'Lead Created', description: 'When a new lead is created or assigned' },
    { key: 'notify_lead_reassigned', label: 'Lead Reassigned', description: 'When a lead is reassigned to another user' },
    { key: 'notify_lead_updated', label: 'Lead Updated', description: 'When a lead status is updated (call outcome, etc.)' },
    { key: 'notify_lead_rejected', label: 'Lead Rejected', description: 'When a lead is rejected by an employee' },
    { key: 'notify_sqd_marked', label: 'SQD Marked', description: 'When a demo is marked as qualified (SQD)' },
    { key: 'notify_deal_created', label: 'Deal Created', description: 'When a new deal is created' },
    { key: 'notify_deal_won', label: 'Deal Won', description: 'When a deal is won (payment received)' },
    { key: 'notify_client_price_update', label: 'Client Price Update', description: 'When a client monthly price is updated' },
    { key: 'notify_client_terminated', label: 'Client Terminated', description: 'When a client is terminated' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await emailConfigAPI.list();
      setConfigs(response.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newConfig.name || !newConfig.from_email) {
      toast({
        variant: 'destructive',
        title: 'Missing details',
        description: 'Please fill in required fields.'
      });
      return;
    }
    
    try {
      await emailConfigAPI.create(newConfig);
      await fetchData();
      setIsCreateOpen(false);
      setNewConfig({
        name: '', provider_type: 'SMTP', smtp_host: '', smtp_port: 587,
        smtp_username: '', smtp_password: '', smtp_use_tls: true,
        aws_access_key: '', aws_secret_key: '', aws_region: 'us-east-1',
        from_email: '', from_name: 'CRM System', reply_to_email: ''
      });
    } catch (error) {
      console.error('Failed to create config:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to create config',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleToggle = async (configId) => {
    try {
      await emailConfigAPI.toggle(configId);
      await fetchData();
    } catch (error) {
      console.error('Failed to toggle config:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to update config',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleDelete = async () => {
    if (!configToDelete) return;

    const ok = await confirm({
      title: 'Delete email config?',
      description: `This will delete "${configToDelete.name}".`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      destructive: true
    });
    if (!ok) return;

    try {
      await emailConfigAPI.delete(configToDelete.id);
      setDeleteConfirmOpen(false);
      setConfigToDelete(null);
      toast({ title: 'Email config deleted' });
      await fetchData();
    } catch (error) {
      console.error('Failed to delete config:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to delete config',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleTest = async () => {
    if (!selectedConfig || !testEmail) return;
    setTesting(true);
    setTestResult(null);
    try {
      const response = await emailConfigAPI.test(selectedConfig.id, testEmail);
      setTestResult(response.data);
    } catch (error) {
      setTestResult({ success: false, message: error.message });
    } finally {
      setTesting(false);
    }
  };

  const handleUpdateNotifications = async (configId, key, value) => {
    try {
      await emailConfigAPI.update(configId, { [key]: value });
      await fetchData();
    } catch (error) {
      console.error('Failed to update notifications:', error);
    }
  };

  const handleViewLogs = async (config) => {
    try {
      const response = await emailConfigAPI.getLogs(config.id);
      setEmailLogs(response.data || []);
      setSelectedConfig(config);
      setIsLogsOpen(true);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    }
  };

  const handleEditConfig = (config) => {
    setSelectedConfig(config);
    setIsEditOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-2 text-slate-500">Loading email configurations...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Email / SMTP Configuration</h1>
          <p className="text-slate-500 dark:text-slate-400">
            Configure email notifications for your team
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button className="bg-slate-800 hover:bg-slate-900" onClick={() => setIsCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Email Config
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-blue-800">Email Notifications</p>
              <p className="text-sm text-blue-700">
                Configure SMTP or AWS SES to send email notifications to employees when important events occur.
                Toggle notifications on/off for each event type.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configs List */}
      {configs.length === 0 ? (
        <Card className="bg-white dark:bg-slate-800 border-slate-200">
          <CardContent className="py-12 text-center">
            <Mail className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="font-medium text-slate-600">No email configurations</p>
            <p className="text-sm text-slate-400">Add an SMTP or AWS SES configuration to enable email notifications</p>
          </CardContent>
        </Card>
      ) : configs.map((config) => (
        <Card key={config.id} className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {config.provider_type === 'SMTP' ? (
                  <Server className="w-5 h-5 text-slate-600" />
                ) : (
                  <Cloud className="w-5 h-5 text-orange-500" />
                )}
                <div>
                  <CardTitle className="text-lg">{config.name}</CardTitle>
                  <p className="text-sm text-slate-500">{config.from_email}</p>
                </div>
                <Badge className={cn(
                  config.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                )}>
                  {config.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => handleViewLogs(config)}>
                  <Eye className="w-4 h-4 mr-1" />
                  Logs
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setSelectedConfig(config); setTestEmail(''); setTestResult(null); }}
                >
                  <Send className="w-4 h-4 mr-1" />
                  Test
                </Button>
                <Button
                  variant={config.is_active ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleToggle(config.id)}
                  className={config.is_active ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                >
                  <Power className="w-4 h-4 mr-1" />
                  {config.is_active ? 'Active' : 'Activate'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-500"
                  onClick={() => { setConfigToDelete(config); setDeleteConfirmOpen(true); }}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="settings" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="settings">Settings</TabsTrigger>
                <TabsTrigger value="notifications">Notifications</TabsTrigger>
                <TabsTrigger value="stats">Statistics</TabsTrigger>
              </TabsList>
              
              <TabsContent value="settings">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <Label className="text-xs text-slate-500">Provider</Label>
                    <p className="font-medium">{config.provider_type}</p>
                  </div>
                  {config.provider_type === 'SMTP' && (
                    <>
                      <div>
                        <Label className="text-xs text-slate-500">Host</Label>
                        <p className="font-medium">{config.smtp_host || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Port</Label>
                        <p className="font-medium">{config.smtp_port || 587}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">TLS</Label>
                        <p className="font-medium">{config.smtp_use_tls ? 'Enabled' : 'Disabled'}</p>
                      </div>
                    </>
                  )}
                  {config.provider_type === 'AWS_SES' && (
                    <div>
                      <Label className="text-xs text-slate-500">Region</Label>
                      <p className="font-medium">{config.aws_region || 'us-east-1'}</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs text-slate-500">From Name</Label>
                    <p className="font-medium">{config.from_name || 'CRM System'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Reply To</Label>
                    <p className="font-medium">{config.reply_to_email || '-'}</p>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="notifications">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {notificationEvents.map(event => (
                    <div key={event.key} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{event.label}</p>
                        <p className="text-xs text-slate-500">{event.description}</p>
                      </div>
                      <Switch
                        checked={config[event.key]}
                        onCheckedChange={(checked) => handleUpdateNotifications(config.id, event.key, checked)}
                      />
                    </div>
                  ))}
                </div>
              </TabsContent>
              
              <TabsContent value="stats">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                    <p className="text-xs text-slate-500">Total Sent</p>
                    <p className="text-2xl font-bold">{config.total_sent || 0}</p>
                  </div>
                  <div className="p-3 bg-emerald-50 rounded-lg">
                    <p className="text-xs text-emerald-600">Successful</p>
                    <p className="text-2xl font-bold text-emerald-700">{config.successful_sent || 0}</p>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="text-xs text-red-600">Failed</p>
                    <p className="text-2xl font-bold text-red-700">{config.failed_sent || 0}</p>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                    <p className="text-xs text-slate-500">Last Sent</p>
                    <p className="text-sm font-medium">
                      {config.last_sent_at ? new Date(config.last_sent_at).toLocaleString('en-IN') : 'Never'}
                    </p>
                  </div>
                </div>
                {config.last_error && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-xs text-red-600 font-medium">Last Error</p>
                    <p className="text-sm text-red-700">{config.last_error}</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      ))}

      {/* Create Modal */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Add Email Configuration
            </DialogTitle>
            <DialogDescription>
              Configure SMTP or AWS SES for email notifications
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={newConfig.name}
                  onChange={(e) => setNewConfig({ ...newConfig, name: e.target.value })}
                  placeholder="e.g., Production SMTP"
                />
              </div>
              <div className="space-y-2">
                <Label>Provider *</Label>
                <Select
                  value={newConfig.provider_type}
                  onValueChange={(v) => setNewConfig({ ...newConfig, provider_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SMTP">SMTP</SelectItem>
                    <SelectItem value="AWS_SES">AWS SES</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {newConfig.provider_type === 'SMTP' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>SMTP Host *</Label>
                    <Input
                      value={newConfig.smtp_host}
                      onChange={(e) => setNewConfig({ ...newConfig, smtp_host: e.target.value })}
                      placeholder="smtp.gmail.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SMTP Port</Label>
                    <Input
                      type="number"
                      value={newConfig.smtp_port}
                      onChange={(e) => setNewConfig({ ...newConfig, smtp_port: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Username</Label>
                    <Input
                      value={newConfig.smtp_username}
                      onChange={(e) => setNewConfig({ ...newConfig, smtp_username: e.target.value })}
                      placeholder="your-email@gmail.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Password</Label>
                    <Input
                      type="password"
                      value={newConfig.smtp_password}
                      onChange={(e) => setNewConfig({ ...newConfig, smtp_password: e.target.value })}
                      placeholder="App password"
                    />
                  </div>
                </div>
              </>
            )}

            {newConfig.provider_type === 'AWS_SES' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>AWS Access Key</Label>
                    <Input
                      value={newConfig.aws_access_key}
                      onChange={(e) => setNewConfig({ ...newConfig, aws_access_key: e.target.value })}
                      placeholder="AKIAIOSFODNN7EXAMPLE"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>AWS Secret Key</Label>
                    <Input
                      type="password"
                      value={newConfig.aws_secret_key}
                      onChange={(e) => setNewConfig({ ...newConfig, aws_secret_key: e.target.value })}
                      placeholder="wJalrXUtnFEMI/K7MDENG/..."
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>AWS Region</Label>
                  <Select
                    value={newConfig.aws_region}
                    onValueChange={(v) => setNewConfig({ ...newConfig, aws_region: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="us-east-1">US East (N. Virginia)</SelectItem>
                      <SelectItem value="us-west-2">US West (Oregon)</SelectItem>
                      <SelectItem value="eu-west-1">EU (Ireland)</SelectItem>
                      <SelectItem value="ap-south-1">Asia Pacific (Mumbai)</SelectItem>
                      <SelectItem value="ap-southeast-1">Asia Pacific (Singapore)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Email Settings</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>From Email *</Label>
                  <Input
                    value={newConfig.from_email}
                    onChange={(e) => setNewConfig({ ...newConfig, from_email: e.target.value })}
                    placeholder="noreply@yourcompany.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>From Name</Label>
                  <Input
                    value={newConfig.from_name}
                    onChange={(e) => setNewConfig({ ...newConfig, from_name: e.target.value })}
                    placeholder="CRM System"
                  />
                </div>
              </div>
              <div className="space-y-2 mt-4">
                <Label>Reply-To Email</Label>
                <Input
                  value={newConfig.reply_to_email}
                  onChange={(e) => setNewConfig({ ...newConfig, reply_to_email: e.target.value })}
                  placeholder="support@yourcompany.com"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleCreate}
              disabled={!newConfig.name || !newConfig.from_email}
              className="bg-slate-800 hover:bg-slate-900"
            >
              Create Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Email Dialog */}
      {selectedConfig && !isLogsOpen && (
        <Dialog open={!!selectedConfig && !isLogsOpen} onOpenChange={() => setSelectedConfig(null)}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Send Test Email
              </DialogTitle>
              <DialogDescription>
                Send a test email to verify your configuration
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="space-y-2">
                <Label>Test Email Address</Label>
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="your-email@example.com"
                />
              </div>
              {testResult && (
                <div className={cn(
                  'mt-4 p-3 rounded-lg',
                  testResult.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'
                )}>
                  <div className="flex items-center gap-2">
                    {testResult.success ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                    <p className={testResult.success ? 'text-emerald-700' : 'text-red-700'}>
                      {testResult.message}
                    </p>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedConfig(null)}>Cancel</Button>
              <Button 
                onClick={handleTest}
                disabled={!testEmail || testing}
                className="bg-slate-800 hover:bg-slate-900"
              >
                {testing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Test
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Logs Modal */}
      <Dialog open={isLogsOpen} onOpenChange={setIsLogsOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Email Logs - {selectedConfig?.name}
            </DialogTitle>
          </DialogHeader>
          
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Event</TableHead>
                <TableHead>To</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {emailLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    No email logs yet
                  </TableCell>
                </TableRow>
              ) : emailLogs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>
                    <Badge variant="outline">{log.event_type}</Badge>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium text-sm">{log.to_email}</p>
                      {log.to_name && <p className="text-xs text-slate-500">{log.to_name}</p>}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate">{log.subject}</TableCell>
                  <TableCell>
                    {log.is_success ? (
                      <Badge className="bg-emerald-100 text-emerald-700">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Sent
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700">
                        <XCircle className="w-3 h-3 mr-1" />
                        Failed
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {new Date(log.created_at).toLocaleString('en-IN')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsLogsOpen(false); setSelectedConfig(null); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={dialogProps.open}
        onOpenChange={dialogProps.onOpenChange}
        title={dialogProps.title}
        description={dialogProps.description}
        confirmText={dialogProps.confirmText}
        cancelText={dialogProps.cancelText}
        destructive={dialogProps.destructive}
        onConfirm={dialogProps.onConfirm}
        onCancel={dialogProps.onCancel}
      />
    </div>
  );
};

export default EmailConfigPage;
